/** Pending-invitations list — was named `MemberList` before Phase 2B-ui.
 *
 * Renamed because the Phase 2B-ui rebuild gave us a proper member list
 * (with DSR action buttons) that consumes the new
 * GET /v1/orgs/members endpoint. This component still shows ONLY
 * pending invites, which is a separate surface. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Pagination } from "@/components/Pagination";
import { ApiPaths, api } from "@/lib/api";
import type { Paginated } from "@/lib/types";

const INVITES_PAGE_SIZE = 30;


type OrgInvite = {
  id: string;
  invited_email: string;
  role_slugs: string[];
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
};


export function InviteList() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const invites = useQuery({
    queryKey: ["org-invites", page],
    queryFn: async () => {
      const res = await api.get<Paginated<OrgInvite>>(ApiPaths.orgInvites, {
        params: { page: String(page), page_size: String(INVITES_PAGE_SIZE) },
      });
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
  const rows = invites.data?.results ?? [];
  const total = invites.data?.count ?? 0;

  const resend = useMutation({
    mutationFn: async (id: string) => {
      await api.post(ApiPaths.orgInviteResend(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-invites"] });
    },
  });

  // Backlog 2026-09-22 — reported: a pending invitation could be
  // resent but never withdrawn, so a wrong address or a person who
  // left sat in the list indefinitely with a live invite link. The
  // revoke endpoint has existed all along (`/invites/<id>/revoke`) and
  // the row already rendered a `revoked` status — only the button was
  // missing.
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await api.post(ApiPaths.orgInviteRevoke(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-invites"] });
    },
  });

  // Reported 2026-09-23 — revoking left the row sitting in the list with
  // no way to clear it, so the block just accumulated dead entries.
  // Only offered once revoked: a pending invite still has a live link
  // someone is waiting on, and an accepted one records how a current
  // member joined. The row is deleted, but an admin-audit event keeps
  // the record that the invitation existed.
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(ApiPaths.orgInviteDetail(id));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org-invites"] });
    },
  });

  return (
    <section>
      <h2 className="mb-2 text-lg font-semibold">{t("ui.inviteList.title")}</h2>
      {invites.isLoading && <p className="text-sm text-nb-text-2">{t("ui.inviteList.loading")}</p>}
      {invites.data && (
        <ul className="divide-y divide-nb-border rounded-xl border border-nb-border bg-nb-surface/40">
          {rows.length === 0 && (
            <li className="px-4 py-3 text-sm text-nb-text-2">{t("ui.inviteList.empty")}</li>
          )}
          {rows.map((i) => {
            const accepted = !!i.accepted_at;
            const revoked = !!i.revoked_at;
            const expired = new Date(i.expires_at) < new Date();
            const status = revoked
              ? t("ui.inviteList.statusRevoked")
              : accepted
                ? t("ui.inviteList.statusAccepted")
                : expired
                  ? t("ui.inviteList.statusExpired")
                  : t("ui.inviteList.statusPending");
            return (
              <li key={i.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div>
                  <div className="font-medium">{i.invited_email}</div>
                  <div className="text-xs text-nb-text-2">
                    {i.role_slugs.join(", ") || t("ui.inviteList.noRoles")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`pill ${expired ? "text-cvp-warn" : ""}`}
                  >
                    {status}
                  </span>
                  {/* Resend is available for still-actionable invites
                      (pending or expired) — not revoked/accepted. */}
                  {!revoked && !accepted && (
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => resend.mutate(i.id)}
                      disabled={resend.isPending}
                    >
                      {t("ui.inviteList.resend")}
                    </button>
                  )}
                  {/* Revoking kills the outstanding link. Same
                      availability as Resend — pointless once accepted
                      or already revoked. */}
                  {!revoked && !accepted && (
                    <button
                      type="button"
                      className="btn-ghost text-xs text-cvp-danger hover:bg-cvp-danger/10"
                      onClick={() => {
                        if (
                          window.confirm(
                            t("ui.inviteList.revokeConfirm", {
                              defaultValue:
                                "Revoke the invitation for {{email}}? Their link stops working immediately.",
                              email: i.invited_email,
                            }),
                          )
                        ) {
                          revoke.mutate(i.id);
                        }
                      }}
                      disabled={revoke.isPending}
                    >
                      {t("ui.inviteList.revoke", "Revoke")}
                    </button>
                  )}
                  {/* Once revoked the row is dead weight — let it go.
                      Not offered for accepted invites: those are how a
                      current member got here. */}
                  {revoked && !accepted && (
                    <button
                      type="button"
                      className="btn-ghost text-xs text-cvp-danger hover:bg-cvp-danger/10"
                      onClick={() => {
                        if (
                          window.confirm(
                            t("ui.inviteList.deleteConfirm", {
                              defaultValue:
                                "Delete the revoked invitation for {{email}}? It disappears from this list.",
                              email: i.invited_email,
                            }),
                          )
                        ) {
                          remove.mutate(i.id);
                        }
                      }}
                      disabled={remove.isPending}
                    >
                      {t("ui.inviteList.delete", "Delete")}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Pagination
        page={page}
        pageSize={INVITES_PAGE_SIZE}
        count={total}
        onPage={setPage}
        disabled={invites.isFetching}
      />
    </section>
  );
}
